import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth';
import toast from 'react-hot-toast';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));

  const loadUser = useCallback(async () => {
    const currentToken = localStorage.getItem('access_token'); // read directly
    if (!currentToken) {
        setLoading(false);
        return;
    }

    try {
        const userData = await authService.getProfile();
        setUser(userData);
    } catch (error) {
        console.error('Failed to load user:', error);
        
        const status = error.response?.status;
        
        if (status === 401 || status === 403) {
            if (status === 403) {
                toast.error(error.response?.data?.message || 'Account suspended');
            }
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setToken(null);
            setUser(null);
        }
        // Network errors / 500s: stay logged in
    } finally {
        setLoading(false);
    }
}, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      const { access_token, refresh_token, user } = response;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      setToken(access_token);
      setUser(user);
      
      // Personalized success message
      if (user.user_type === 'admin') {
        toast.success('Welcome back, Admin!');
      } else {
        toast.success(`Welcome back, ${user.first_name || user.username}!`);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMsg = 'Login failed. Please check your credentials.';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        // Handle different HTTP status codes with specific messages
        switch (status) {
          case 401:
            errorMsg = data?.message || 'Invalid username or password';
            break;
          case 403:
            // Enhanced verification message
            if (data?.message?.toLowerCase().includes('verify') || 
                data?.message?.toLowerCase().includes('verified') ||
                data?.message?.toLowerCase().includes('verification')) {
              errorMsg = 'Your account is pending verification. Please check your email for the verification link. If you haven\'t received it, click "Resend Verification" below.';
            } else if (data?.message?.toLowerCase().includes('suspended')) {
              errorMsg = 'Your account has been suspended. Please contact support for assistance.';
            } else {
              errorMsg = data?.message || 'Access denied. Please contact support.';
            }
            break;
          case 404:
            errorMsg = data?.message || 'User not found';
            break;
          case 429:
            errorMsg = 'Too many login attempts. Please try again later.';
            break;
          case 500:
            errorMsg = 'Server error. Please try again later.';
            break;
          default:
            errorMsg = data?.message || data?.error || `Login failed (${status})`;
        }
      } else if (error.request) {
        // Request made but no response received
        errorMsg = 'Unable to connect to server. Please check your internet connection.';
      } else {
        // Something else happened
        errorMsg = error.message || 'An unexpected error occurred';
      }
      
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      const message = response.message || 'Registration successful! Please check your email to verify your account.';
      toast.success(message);
      return { success: true, message };
    } catch (error) {
      console.error('Registration error:', error);
      
      let errorMsg = 'Registration failed. Please try again.';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            // Validation errors
            if (data.errors) {
              // Handle multiple validation errors
              const validationErrors = Object.values(data.errors).flat().join(', ');
              errorMsg = validationErrors || data?.message || 'Invalid registration data';
            } else {
              errorMsg = data?.message || 'Invalid registration data';
            }
            break;
          case 409:
            // Conflict - user already exists
            if (data?.message?.toLowerCase().includes('email')) {
              errorMsg = 'Email already registered. Please use a different email or try logging in.';
            } else if (data?.message?.toLowerCase().includes('username')) {
              errorMsg = 'Username already taken. Please choose a different username.';
            } else {
              errorMsg = data?.message || 'User already exists';
            }
            break;
          case 422:
            errorMsg = data?.message || 'Validation failed. Please check your input.';
            break;
          case 500:
            errorMsg = 'Server error. Please try again later.';
            break;
          default:
            errorMsg = data?.message || data?.error || `Registration failed (${status})`;
        }
      } else if (error.request) {
        errorMsg = 'Unable to connect to server. Please check your internet connection.';
      } else {
        errorMsg = error.message || 'An unexpected error occurred';
      }
      
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const refreshToken = async () => {
    try {
      const refresh_token = localStorage.getItem('refresh_token');
      if (!refresh_token) throw new Error('No refresh token');
      
      const response = await authService.refreshToken(refresh_token);
      const { access_token } = response;
      
      localStorage.setItem('access_token', access_token);
      setToken(access_token);
      return access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      
      // Clear invalid tokens
      logout();
      
      let errorMsg = 'Session expired. Please login again.';
      if (error.response?.status === 401) {
        errorMsg = 'Your session has expired. Please login again.';
      }
      
      toast.error(errorMsg);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    isAuthenticated: !!user,
    isAdmin: user?.user_type === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};